import {
  type Action,
  type CaseReducer,
  createSelector,
  type PayloadAction,
  type PayloadActionCreator,
  type PrepareAction,
  createAction as toolkitCreateAction,
} from '@reduxjs/toolkit';
import type { ActionCreator as ReduxActionCreator } from 'redux';
import produce, { applyPatches, current, enablePatches, original, produceWithPatches } from 'immer';
import { isEqual, isString, isUndefined, merge } from 'lodash';
import { createCachedSelector } from 're-reselect';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import type { Observable } from 'rxjs';

import { isLocalhost } from 'utils/client-utils';
import { debugCheck } from 'utils/debug-check';
import { isShallowArrayEqual } from 'utils/is-shallow-array-equal';
import { exposeToGlobalConsole } from 'utils/react/expose-to-global-console';

// Debug utility - breaks when action creator is CALLED (not dispatched)
// Usage: globals.debugActions = ['pinHud', 'addSession']
const wrapActionCreatorForDebug = <T extends (...args: any[]) => any>(actionCreator: T): T => {
  const wrapped = (...args: any[]) => {
    const patterns = (window as any).globals?.debugActions || [];
    if (patterns.some((p: string) => (actionCreator as any).type?.includes(p))) {
      // eslint-disable-next-line no-debugger
      debugger; // Call stack shows where the action creator was called!
    }
    return actionCreator(...args);
  };

  // Copy over Redux Toolkit properties
  Object.assign(wrapped, actionCreator);
  return wrapped as T;
};

import type { UseDispatchActionMeta } from 'utils/react/use-dispatch';
import { storage } from 'utils/storage';

import type { AppServices } from './app-services';
import type { RootReduxDispatch, RootReduxState } from './app-store';
import { createGlobalAsyncAction } from './create-async-action';

export const MARK_AS_REDUX_SELECTOR = 'MARK_AS_REDUX_SELECTOR';

export type UndoableState = {
  undo: {
    [stackId: string]: {
      pointer: number;
      stack: any[][];
    };
  };
};

const defaultUndoableState = {
  undo: {},
};

const createDefaultUndoStack = () => ({
  pointer: 0,
  stack: [],
});

type WithUndoOption<State, ActionType> =
  | boolean
  | string
  | ((state: State, action: ActionType) => boolean | string);

export type Thunk<S, PayloadType, ReturnType> = (
  dispatch: RootReduxDispatch,
  getState: () => S,
  payload: PayloadType,
  extraArgument: {
    injector: any;
    ngxsStore: any;
    ngxsActions$: any;
    action$: Observable<Action<any>>;
    services: AppServices;
  },
) => ReturnType;

enablePatches();

export function createGlobalSelector<SelectorFn extends (state: RootReduxState, ...args: any[]) => any>(
  selectorFn: SelectorFn,
) {
  return markAsReduxSelector(selectorFn);
}

export const createGlobalParametricMemoizedSelector = ((...args: any[]) => {
  const outputSelector = (createCachedSelector as any)(...args);
  return (...args: any[]) => markAsReduxSelector(outputSelector(...args));
}) as typeof createCachedSelector;

const createGlobalSelectorWithSelectorCreator = (selectorCreator: (...args: any[]) => any) =>
  ((...args: any[]) => markAsReduxSelector(selectorCreator(...args))) as typeof createSelector;

const markAsReduxSelector = <T>(selectorFn: T) => {
  (selectorFn as any)[MARK_AS_REDUX_SELECTOR] = true;
  return selectorFn;
};

const globalCreatedActionTypes = new Set();
export const createGlobalAction = <PayloadType = void, Type extends string = string>(type: Type) => {
  debugCheck(!globalCreatedActionTypes.has(type), `Unexpected duplicated global action type ${type}.`);
  globalCreatedActionTypes.add(type);
  const action = toolkitCreateAction<PayloadType, Type>(type);

  // So we can access, e.g., globals.actions.restartApp
  exposeToGlobalConsole({ actions: { ...(window as any)['globals'].actions, [type]: action } });

  return action;
};

export const createGlobalThunk = <S = RootReduxState, PayloadType = void, ReturnType = void>(
  name: string,
  thunk: Thunk<S, PayloadType, ReturnType>,
) => {
  const actionCreator = (payload: PayloadType) => {
    const outer = (dispatch: any, getState: any, extraArgument: any) =>
      thunk(dispatch, getState, payload, {
        ...(extraArgument ?? {}),
        meta: (outer as any).meta,
      });
    outer.type = name;
    return outer;
  };
  actionCreator.type = name;

  return actionCreator;
};

export type Selector<S, R, Args extends unknown[]> = (state: S, ...args: Args) => R;

export function createSlice<
  StateType,
  T extends string,
  UndoActionPayload,
  K extends (
    | Extract<keyof StateType, string | number>
    | { key: Extract<keyof StateType, string | number>; version: number }
  )[],
>(
  this: void,
  name: T,
  initialState?: StateType,
  options: {
    undoStackKeyFn?: (state: StateType, action: PayloadAction<UndoActionPayload>) => string;
    persistKeys?: K;
  } = {},
) {
  type StateSlice = { [k in T]: StateType };

  const createdActionTypes = new Set();

  function createAction<PayloadType = void, SubType extends string = string>(
    this: void,
    subType: SubType,
  ): PayloadActionCreator<
    PayloadType,
    `${T}/${SubType}`,
    (...args: any[]) => { payload: PayloadType; meta: UseDispatchActionMeta }
  >;
  function createAction<PA extends PrepareAction<any>, SubType extends string = string>(
    this: void,
    subType: SubType,
    prepare: PA,
  ): PayloadActionCreator<
    ReturnType<PA>['payload'],
    `${T}/${SubType}`,
    (...args: any[]) => ReturnType<PA> & { meta: UseDispatchActionMeta }
  >;
  function createAction(subType: string, prepare?: PrepareAction<any>) {
    debugCheck(
      !createdActionTypes.has(subType),
      `Unexpected duplicated action type ${subType} in ${name} slice.`,
    );
    createdActionTypes.add(subType);
    const actionCreator = prepare
      ? toolkitCreateAction(`${name}/${subType}` as const, prepare)
      : toolkitCreateAction(`${name}/${subType}` as const);
    if (!isLocalhost) return actionCreator;
    return wrapActionCreatorForDebug(actionCreator);
  }

  function createThunk<S = { [t in T]: StateType }, PayloadType = void, ReturnType = void>(
    this: void,
    // Maybe someday I'll figure out what to do with this for debugging.
    thunkName: string,
    thunk: Thunk<S, PayloadType, ReturnType>,
  ) {
    return createGlobalThunk<S, PayloadType, ReturnType>(`${name}/${thunkName}`, thunk);
  }
  const wrappedCreateSelector = <R, Args extends unknown[]>(
    selectorFn: Selector<StateSlice, R, Args>,
  ): Selector<StateSlice, R, Args> => markAsReduxSelector(selectorFn);

  const undoAction = createAction<UndoActionPayload>('undo' as const);
  const redoAction = createAction<UndoActionPayload>('redo' as const);
  const getIsRedoable = wrappedCreateSelector((state, payload: UndoActionPayload) => {
    const stackKey = `${options.undoStackKeyFn!(state[name], undoAction(payload))}-redo`;
    return (state[name] as UndoableState).undo[stackKey]?.pointer > 0;
  });
  const makePersistStorageKey = (key: string | number) => `persisted-${name}-${key}`;

  type PersistedKey =
    | Extract<K[number], keyof StateType>
    | Extract<K[number], { key: keyof StateType }>['key'];
  function getPersistedValue<V extends PersistedKey>(this: void, key: V) {
    return storage.get(makePersistStorageKey(key as any) as any) as StateType[V] | undefined;
  }

  return {
    createParametricMemoizedSelector: createGlobalParametricMemoizedSelector,

    createMemoizedSelector: createGlobalMemoizedSelector,

    getPersistedValue,

    createSelector: wrappedCreateSelector,

    // Pull out this func to do some TS overloading magic.
    createAction,

    undoActions: {
      undo: undoAction,
      redo: redoAction,
      getIsRedoable,
    },

    createAsyncAction<PayloadType = void, ResponseType = void>(this: void, subType: string) {
      debugCheck(
        !createdActionTypes.has(subType),
        `Unexpected duplicated action type ${subType} in ${name} slice.`,
      );
      createdActionTypes.add(subType);
      return createGlobalAsyncAction<PayloadType, ResponseType>(`${name}/${subType}`);
    },

    createThunk,

    createToggleThunk<ThunkPayload = void>(
      this: void,
      {
        actionCreator,
        selector,
      }: {
        actionCreator: (payload: boolean) => any;
        selector: Selector<StateSlice, boolean, [ThunkPayload, { meta: UseDispatchActionMeta }]>;
      },
    ) {
      return createThunk(
        `makeToggleThunk/${actionCreator.name}`,
        (dispatch: RootReduxDispatch, getState, payload: ThunkPayload, extraArgument) => {
          const action = actionCreator(!selector(getState(), payload, extraArgument as any));
          action.meta = (extraArgument as any)?.meta;
          dispatch(action);
        },
      );
    },

    createReducer(this: void) {
      const handlers: Record<
        string,
        { caseReducer: (...args: any) => any; options: { withUndo?: WithUndoOption<StateType, any> } }
      > = {};
      const frozenInitialState = produce(
        merge(
          { ...initialState },
          options.undoStackKeyFn && defaultUndoableState,
          options.persistKeys?.reduce(
            (memo, maybeVersionedKey) => {
              const { key, version } =
                typeof maybeVersionedKey === 'object'
                  ? maybeVersionedKey
                  : {
                      key: maybeVersionedKey as Extract<keyof StateType, string | number>,
                      version: undefined,
                    };
              const item = getPersistedValue(key as any);
              const storedVersion = getPersistedValue(`${key}-version` as any);
              if (item !== null && item !== undefined && storedVersion === version) memo[key] = item;
              return memo;
            },
            {} as Partial<StateType>,
          ),
        ),
        () => {},
      );

      // The merge's inferred shape includes optional persistence/undo machinery;
      // callers accept the declared slice state, including a prior reducer result.
      const reducer = (state: StateType = frozenInitialState as StateType, action: Action): StateType => {
        if (!handlers[action.type]) return state;
        const {
          caseReducer,
          options: { withUndo },
        } = handlers[action.type];

        const handler = !options.persistKeys
          ? caseReducer
          : (draftState: any, action: any) => {
              caseReducer(draftState, action);

              const originalState = original(draftState);
              const changedState = current(draftState);
              options.persistKeys!.forEach((maybeVersionedKey) => {
                const { key, version } =
                  typeof maybeVersionedKey === 'object'
                    ? maybeVersionedKey
                    : {
                        key: maybeVersionedKey as Extract<keyof StateType, string | number>,
                        version: undefined,
                      };
                if (originalState[key] === changedState[key]) return;
                storage.set(`persisted-${name}-${key}` as any, changedState[key]);
                if (!isUndefined(version)) storage.set(`persisted-${name}-${key}-version` as any, version);
              });
            };

        const undoStackId = intepretWithUndoOption(withUndo as any, state, action);
        if (undoStackId) {
          const [nextState, _forwardPatch, reversePatch] = produceWithPatches(state, (draftState) =>
            handler(draftState, action),
          );
          return produce(nextState, (draftState: UndoableState) => {
            debugCheck(!!draftState.undo, 'Expected reducer employing `withUndo` to extend UndoableState.');
            if (!draftState.undo[undoStackId]) draftState.undo[undoStackId] = createDefaultUndoStack();
            // We track the reversePatches of the undo action via the
            // `${undoStackId}-redo` because consumers can pick and choose which
            // actions to include `withUndo`. If you naively store the
            // forwardPatch to reverse the undo (aka redo), there's a subtle
            // issue because you'd need to replay all actions between the
            // originating action and the undo action to get back to what the
            // user thinks the state is. Instead, we just undo the state change
            // we made with the undo, ensuring the state is back to where it was
            // when the user clicked undo.
            if (!draftState.undo[`${undoStackId}-redo`])
              draftState.undo[`${undoStackId}-redo`] = createDefaultUndoStack();
            draftState.undo[undoStackId].pointer++;
            draftState.undo[undoStackId].stack.length = draftState.undo[undoStackId].pointer;
            draftState.undo[undoStackId].stack[draftState.undo[undoStackId].pointer - 1] = reversePatch;
          }) as any;
        }

        return handler ? (produce(state, (draftState) => handler(draftState, action)) as any) : state;
      };

      const on = <ActionCreator extends ReduxActionCreator<Action> & { type: string }>(
        actionCreator: ActionCreator,
        caseReducer: CaseReducer<StateType, ReturnType<ActionCreator>>,
        options: { withUndo?: WithUndoOption<StateType, ReturnType<ActionCreator>> } = {},
      ) => {
        handlers[actionCreator.type] = {
          caseReducer,
          options,
        };
        return reducer as ExtendedReducer;
      };

      // The undoStackKeyFn allows you to dispatch undo or redo actions and line
      // it up with whichever undo stack you're targeting. This will correspond
      // with one or many of the keys that are being tracked by the withUndo
      // property of reducers.
      if (options.undoStackKeyFn) {
        const undoForStackKey = (state: UndoableState, stackKey: string) => {
          const undo = state.undo[stackKey];
          if (!undo || undo.pointer === 0) return;
          undo.pointer--;
          const reversePatches = undo.stack[undo.pointer];
          applyPatches(state, reversePatches);
        };

        on(
          undoAction,
          (state, action) => {
            undoForStackKey(state as any, options.undoStackKeyFn!(state as any, action as any));
          },
          {
            withUndo: (state, action) => {
              const stackKey = options.undoStackKeyFn!(state, action as any);
              const undo = (state as UndoableState).undo[stackKey];
              // Only track in redo stack if there's actually something to undo
              if (!undo || undo.pointer === 0) return false;
              return `${stackKey}-redo`;
            },
          },
        );

        on(redoAction, (state, action) => {
          undoForStackKey(state as any, `${options.undoStackKeyFn!(state as any, action as any)}-redo`);
        });
      }

      type ExtendedReducer = typeof reducer & { on: typeof on };
      (reducer as any).on = on;
      return reducer as ExtendedReducer;
    },
  };
}

// Just some ergonomics magic to take an overloaded variable and do the right
// thing with it.
const intepretWithUndoOption = (
  withUndo: WithUndoOption<any, any>,
  state: any,
  action: any,
): false | string => {
  if (!withUndo) return false;
  if (withUndo === true) return 'default';
  if (isString(withUndo)) return withUndo;
  const result = withUndo(state, action);
  return intepretWithUndoOption(result as any, state, action);
};

/**
 * This will work exactly the same as the default createSelector for
 * memoization, except when comparing a shallow array, in which case it will
 * recurse one level and then check reference equality for each element. This is
 * useful for avoiding re-renders but you need to calculate an array of IDs in a
 * selector.
 */
export const createShallowArrayEqualSelector = createSelectorCreator(defaultMemoize, isShallowArrayEqual);
const createIsEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const createGlobalMemoizedSelector = createGlobalSelectorWithSelectorCreator(createSelector);

export const createGlobalMemoizedIsEqualSelector =
  createGlobalSelectorWithSelectorCreator(createIsEqualSelector);
export const createGlobalMemoizedShallowArrayEqualSelector = createGlobalSelectorWithSelectorCreator(
  createShallowArrayEqualSelector,
);
